import type { ReactNode } from 'react'
import styles from './Table.module.css'

export interface Column<T> {
  key: string
  header: ReactNode
  render: (row: T) => ReactNode
  /** Right-align money and counts so decimal points line up down the column. */
  align?: 'left' | 'right' | 'center'
  width?: string
  /** Hidden below ~900px. For columns that are useful but not essential. */
  secondary?: boolean
}

interface TableProps<T> {
  columns: Array<Column<T>>
  rows: T[]
  rowKey: (row: T) => string
  loading?: boolean
  /** Shown when there are no rows AND no filters are active. */
  emptyMessage?: ReactNode
  /** Shown when filters produced nothing — a different situation entirely. */
  emptyFilteredMessage?: ReactNode
  isFiltered?: boolean
  onRowClick?: (row: T) => void
  error?: Error | null
  onRetry?: () => void
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyMessage = 'Nothing here yet.',
  emptyFilteredMessage = 'No results match these filters.',
  isFiltered = false,
  onRowClick,
  error,
  onRetry,
}: TableProps<T>) {
  // *** ERROR BEFORE LOADING ***
  // A failed refetch keeps stale rows in cache. Showing them under a spinner
  // would present outdated data as if it were current.
  if (error) {
    return (
      <div className={styles.stateBox} role="alert">
        <p className={styles.stateTitle}>Could not load this list</p>
        <p className={styles.stateBody}>{error.message}</p>
        {onRetry && (
          <button type="button" className={styles.retry} onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    )
  }

  // Skeleton rows rather than a spinner: the table keeps its height, so the
  // page does not jump when data arrives.
  if (loading && rows.length === 0) {
    return (
      <div className={styles.wrapper}>
        <table className={styles.table}>
          <TableHead columns={columns} />
          <tbody>
            {Array.from({ length: 5 }, (_, index) => (
              <tr key={index} className={styles.skeletonRow}>
                {columns.map((column) => (
                  <td key={column.key} className={styles.cell}>
                    <span className={styles.skeleton} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className={styles.stateBox}>
        <p className={styles.stateBody}>
          {isFiltered ? emptyFilteredMessage : emptyMessage}
        </p>
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      {/* Refetch dims the table rather than replacing it, so the admin keeps
          their place while newer data loads. */}
      <table className={`${styles.table} ${loading ? styles.refreshing : ''}`}>
        <TableHead columns={columns} />
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={onRowClick ? styles.clickable : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              // Keyboard parity: a clickable row must be reachable by tab and
              // activated by Enter, or the table is mouse-only.
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === 'Enter') onRowClick(row)
                    }
                  : undefined
              }
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={[
                    styles.cell,
                    column.align === 'right' ? styles.right : '',
                    column.align === 'center' ? styles.center : '',
                    column.secondary ? styles.secondary : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TableHead<T>({ columns }: { columns: Array<Column<T>> }) {
  return (
    <thead>
      <tr>
        {columns.map((column) => (
          <th
            key={column.key}
            scope="col"
            style={column.width ? { width: column.width } : undefined}
            className={[
              styles.headCell,
              column.align === 'right' ? styles.right : '',
              column.align === 'center' ? styles.center : '',
              column.secondary ? styles.secondary : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {column.header}
          </th>
        ))}
      </tr>
    </thead>
  )
}
